import { useQuery } from '@apollo/client'
import _ from 'lodash'
import cloneDeep from 'lodash/cloneDeep'
import { useMemo } from 'react'

import { IDirector } from 'pared/data/getDirectors'

import useConfig from '../../hooks/useLossPreventionConfig'
import {
  IListLocationGroupCashControlDetailsNodeType,
  IListLocationGroupCashControlDetailsType,
  LIST_LOCATION_GROUP_CASH_CONTROL_DETAILS,
  LIST_LOCATION_GROUP_METRIC_VALUES,
} from '../gql'

const useLocationGroupCash = (
  directors: IDirector[],
  locationGroupIds?: number[],
  startDate?: string,
  endDate?: string,
  locationGroupType?: string,
) => {
  const config = useConfig()
  const getColorFromValue = config?.cashControlTable?.getColorFromValue
  const metrics = useMemo(
    () =>
      Object.values(config.cashControlTable.cashControlDataFromMetrics || {}),
    [config.cashControlTable],
  )

  let iFilter: any = null
  if (locationGroupType) {
    iFilter = { location_group_types: [locationGroupType], metrics }
  } else if (locationGroupIds && locationGroupIds.length > 0) {
    iFilter = { location_group_ids: [...locationGroupIds], metrics }
  }

  const { data: cashData, loading: cashLocationGroupLoading } =
    useQuery<IListLocationGroupCashControlDetailsType>(
      LIST_LOCATION_GROUP_CASH_CONTROL_DETAILS,
      {
        variables: {
          iFilter,
          iStartDate: startDate,
          iEndDate: endDate,
          useMetrics: metrics.length > 0,
        },
        skip: !startDate || !endDate || !iFilter,
      },
    )

  const { data: metricData, loading: metricDataLoading } = useQuery(
    LIST_LOCATION_GROUP_METRIC_VALUES,
    {
      variables: {
        iLocationGroupTypes: [locationGroupType],
        iMetricCodes: ['pnl_cash_over/short', 'net_sales'],
        iStartDate: startDate,
        iEndDate: endDate,
      },
      skip: !startDate || !endDate || !iFilter || !getColorFromValue,
    },
  )

  const metricDataNodes: {
    locationGroupId: number
    metricData: { [key: string]: { value: number } }
  }[] = metricData?.listLocationGroupMetricValues?.nodes ?? []

  const cashLocationGroupData =
    useMemo(() => {
      const rawData = cashData?.listLocationGroupCashControlDetails?.nodes || []
      const rawDataFromMetrics = cashData?.listLocationGroupMetricValues?.nodes

      const tableData = rawData?.map(
        (row: IListLocationGroupCashControlDetailsNodeType) => {
          const locationGroupId = row.locationGroupId
          const director = directors.find(
            (d: IDirector) => d.locationGroupId === locationGroupId,
          )

          const metricDataNode = metricDataNodes.find(
            (node) => locationGroupId === node.locationGroupId,
          )
          const rawDataFromMetric = rawDataFromMetrics?.find(
            (node) => locationGroupId === node.locationGroupId,
          )

          const customData = cloneDeep(row?.cashControlDetails)
          if (getColorFromValue) {
            customData.Overrings.checkPercent = {
              value: customData.Overrings.checkPercent as number,
              color: getColorFromValue(
                'Overrings',
                customData.Overrings.checkPercent as number,
              ),
            }

            customData.Refunds.checkPercent = {
              value: customData.Refunds.checkPercent as number,
              color: getColorFromValue(
                'Refunds',
                customData.Refunds.checkPercent as number,
              ),
            }

            const cashOverOrShortAmount =
              metricDataNode?.metricData?.['pnl_cash_over/short']?.value
            const netSalesAmount =
              metricDataNode?.metricData?.['net_sales']?.value

            if (getColorFromValue && cashOverOrShortAmount && netSalesAmount) {
              customData.cashOverOrShort = {
                ...customData.cashOverOrShort,
                amount:
                  metricDataNode?.metricData?.['pnl_cash_over/short']?.value ||
                  0,
                checkPercent: {
                  value: (100 * cashOverOrShortAmount) / netSalesAmount,
                  color: getColorFromValue(
                    'cashovershort',
                    (100 * cashOverOrShortAmount) / netSalesAmount,
                  ),
                },
              }
            }
          }

          if (config.cashControlTable.cashControlDataFromMetrics)
            Object.entries(
              config.cashControlTable.cashControlDataFromMetrics,
            ).forEach(([key, value]) => {
              _.set(
                customData,
                key,
                rawDataFromMetric?.metricData?.[value]?.value,
              )
            })

          return {
            locationGroupName: row.locationGroupName,
            directorId: director?.directorId || 0,
            directorName: [director?.firstName || '', director?.lastName || '']
              .filter(Boolean)
              .join(' '),
            ...customData,
          }
        },
      )

      return tableData
    }, [
      cashData,
      metricData,
      directors,
      locationGroupIds,
      startDate,
      endDate,
      locationGroupType,
    ]) || []

  return {
    cashLocationGroupData,
    cashLocationGroupLoading: cashLocationGroupLoading || metricDataLoading,
  }
}

export default useLocationGroupCash
